import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Circle, AlertCircle, Archive, XCircle, Clock } from 'lucide-react';

const TicketList = ({ tickets, selectedTicketId, onSelectTicket, loading, error }) => {
  // Status badge component
  const StatusBadge = ({ status }) => {
    switch (status) {
      case 'open':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle size={12} className="mr-1" />
            Open
          </span>
        );
      case 'in-progress':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock size={12} className="mr-1" />
            In Progress
          </span>
        );
      case 'resolved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle size={12} className="mr-1" />
            Resolved
          </span>
        );
      case 'closed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Archive size={12} className="mr-1" />
            Closed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  // Priority indicator component
  const PriorityIndicator = ({ priority }) => {
    switch (priority) {
      case 'high':
        return <span className="w-2 h-2 rounded-full bg-red-500"></span>;
      case 'medium':
        return <span className="w-2 h-2 rounded-full bg-yellow-500"></span>;
      case 'low':
        return <span className="w-2 h-2 rounded-full bg-green-500"></span>;
      default:
        return <span className="w-2 h-2 rounded-full bg-gray-500"></span>;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // If less than 7 days ago, show relative time
    if (diffDays < 7) {
      if (diffDays === 0) {
        const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
        if (diffHours === 0) {
          const diffMinutes = Math.floor(diffTime / (1000 * 60));
          return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
        }
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      }
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
    
    // Otherwise show the date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <AlertCircle size={24} className="mb-2" />
        <p>{error}</p>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle size={24} className="mb-2" />
        <p>No tickets found</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
      <ul className="space-y-2">
        {tickets.map((ticket) => (
          <motion.li
            key={ticket.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            onClick={() => onSelectTicket(ticket)}
            className={`p-3 rounded-md cursor-pointer transition-colors ${
              selectedTicketId === ticket.id
                ? 'bg-blue-50 border border-blue-200'
                : 'bg-white border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center">
                <PriorityIndicator priority={ticket.priority} />
                <span className="ml-2 text-xs font-medium text-gray-500">{ticket.id}</span>
              </div>
              <StatusBadge status={ticket.status} />
            </div>
            <h3 className="font-medium text-gray-900 line-clamp-1">{ticket.title}</h3>
            <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
              <span>{ticket.employer?.name || 'Unknown Employer'}</span>
              <span>{formatDate(ticket.createdAt)}</span>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              <span className="inline-block px-2 py-0.5 bg-gray-100 rounded-full capitalize">{ticket.category}</span>
              {ticket.assignedTo && (
                <span className="inline-block ml-2 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
                  Assigned
                </span>
              )}
            </div>
          </motion.li>
        ))}
      </ul>
    </div>
  );
};

export default TicketList;
